import { getCSS, remCSS } from '@gimlite/common';
import classNames from 'classnames';
import React, { memo, useContext, useState } from 'react';
import { ColorType } from '../../types/component.type';
import {
  ButtonInput,
  ButtonInputType,
} from '../buttonInput/buttonInput.component';
import { FormItemContext } from '../form/form.component';
import { Icon, IconType } from '../icon/icon.component';
import './input.component.scss';
import { Spinner } from '../spinner/spinner.component';
import { Row } from '../row/row.component';

const sizeMapper = {
  small: {
    padding: `${remCSS(8)} ${remCSS(16)}`,
  },
  medium: {
    padding: `${remCSS(16)} ${remCSS(16)}`,
  },
} as const satisfies Record<string, { padding: string }>;

const themeMapper = {
  light: {
    borderColor: 'deep_indigo_10',
    backgroundColor: 'white_100',
    color: 'black_70',
  },
  dark: {
    borderColor: 'white_10',
    backgroundColor: 'white_10',
    color: 'white_100',
  },
} as const satisfies Record<
  string,
  { borderColor: string; backgroundColor: string; color: string }
>;

const borderStatusMapper = {
  error: 'alert_error',
  success: 'alert_success',
} as const satisfies Record<string, ColorType>;

const textTransformMapper = {
  uppercase: (value?: string | null) => value?.toUpperCase() || undefined,
  lowercase: (value?: string | null) => value?.toLowerCase() || undefined,
  capitalize: (value?: string | null) =>
    value && value.length > 0
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : undefined,
  none: (value?: string | null) => value || undefined,
} as const satisfies Record<string, (value: string) => string | undefined>;

export declare namespace InputType {
  type Props = {
    className?: string | null;
    placeholder?: string | null;
    icon?: IconType.Props['variant'] | null;
    onChange?: (value: string | null) => void;
    onBlur?: () => void;
    size?: keyof typeof sizeMapper;
    theme?: keyof typeof themeMapper;
    value?: string | null;
    defaultValue?: string | null;
    type?: 'text' | 'number' | 'email' | 'url';
    textTransform?: keyof typeof textTransformMapper | null;
    disabled?: boolean;
    clear?: boolean;
    isLoading?: boolean;
    onClear?: () => void;
    action?: ButtonInputType.Props;
    name?: string;
  };
}

export const Input = memo(
  React.forwardRef<HTMLInputElement, InputType.Props>(
    (
      {
        className,
        placeholder,
        icon,
        onChange,
        size = 'medium',
        value,
        type = 'text',
        defaultValue,
        onBlur,
        onClear,
        textTransform,
        action,
        disabled,
        clear = false,
        theme = 'light',
        isLoading: isLoadingProps = false,
        name,
      }: InputType.Props,
      ref,
    ) => {
      const formItemContext = useContext(FormItemContext);
      const id = React.useId();
      const [isLoading, setIsLoading] = useState<boolean>(false);

      const isDisabled = disabled || isLoading;

      return (
        <div className="input">
          <label
            htmlFor={id}
            className={classNames(
              'input__wrapper',
              {
                'input__wrapper--action': action,
                'input__wrapper--disabled': isDisabled || isLoadingProps,
              },
              className,
            )}
            style={{
              padding: sizeMapper[size].padding,
              borderColor: formItemContext.borderStatus
                ? getCSS(
                    `--color-${borderStatusMapper[formItemContext.borderStatus]}`,
                  )
                : getCSS(`--color-${themeMapper[theme].borderColor}`),
              backgroundColor: getCSS(
                `--color-${themeMapper[theme].backgroundColor}`,
              ),
            }}
          >
            {!isLoadingProps && <input
              id={id}
              name={name}
              value={
                value
                  ? (textTransform
                      ? textTransformMapper[textTransform](value)
                      : value) || ''
                  : textTransform
                    ? textTransformMapper[textTransform](defaultValue)
                    : defaultValue || ''
              }
              style={{
                textTransform: textTransform || undefined,
                color: getCSS(`--color-${themeMapper[theme].color}`),
              }}
              disabled={isDisabled || isLoadingProps}
              className="input__wrapper__native"
              placeholder={placeholder || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (textTransform) {
                  onChange?.(textTransformMapper[textTransform](value) || null);
                } else {
                  onChange?.(value || null);
                }
              }}
              onBlur={onBlur}
              type={type}
              ref={ref}
            />
            }
            {isLoadingProps && (
              <Row
                justifyContent="center"
                width="max-content"
                alignItems="center"
              >
                <Spinner variant="dotStream" size="medium" color="black_50" />
              </Row>
            )}
            {clear &&
              !isLoadingProps &&
              !isDisabled &&
              value &&
              value?.length > 0 && (
                <div
                  onClick={() => {
                    onClear?.();
                    onChange?.(null);
                  }}
                  className="input__wrapper__clear"
                >
                  <Icon size="xxs" variant="cross" color="black_50" />
                </div>
              )}
            {icon && !isLoadingProps && (
              <Icon variant={icon} color="deep_indigo_50" />
            )}
          </label>
          {action && (
            <ButtonInput
              {...action}
              isLoading={isLoading}
              onLoading={(isLoading) => setIsLoading(isLoading)}
            />
          )}
        </div>
      );
    },
  ),
);
